import { FC, useMemo, useState, type JSX } from 'react';
import NextLink from 'next/link';
import { useParams } from 'next/navigation';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { LineItem } from 'shared/types/wishlist/LineItem';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import ProductBadge from 'components/padi-product/badge';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { ProjectConfig } from 'types/project-config';
import Image from 'frontastic/lib/image';
import Prices from './prices';
import useTrack from './useTrack';

interface CertProductTileProps {
  product: Product;
  index?: number;
  titleHtag?: string;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  projectConfig?: ProjectConfig;
  imagePriority?: boolean;
}

const CertProductTile: FC<CertProductTileProps> = ({
  product,
  index = 0,
  titleHtag,
  onClick,
  isSearchResult = false,
  disableWishlistButton = false,
  imagePriority = false,
}) => {
  const { locale } = useParams();
  const { ref } = useTrack({ product });

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;

  const [selectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);

  const discountedPrice = selectedVariant?.discountedPrice;

  const excerpt = useMemo(() => {
    const field = product.variants?.[0].attributes?.['card-excerpt'];
    return typeof field === 'string' ? field : '';
  }, [product]);

  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);

  const productUrl = useMemo(() => {
    if (!product._url) return `/${(product.name ?? '').toLowerCase().replace(/\s+/g, '-')}/p/${selectedVariant.sku}`;

    const url = `${product._url}` + (isSearchResult ? '?sr=1' : '');
    return url;
  }, [product, selectedVariant, isSearchResult]);

  const TitleHtag = titleHtag ? (titleHtag as keyof JSX.IntrinsicElements) : 'div';

  // Helper to safely fetch attribute values with a default value
  const getAttributeValue = (attr: any, defaultValue = '') => {
    if (Array.isArray(attr)) return attr[0]?.label ?? defaultValue;
    else return attr?.label ?? defaultValue;
  };
  const cardType = getAttributeValue(product?.variants?.[0]?.attributes?.['card-type']);

  const isCertificationCard = product?.productType === 'certification-card';

  const eyeBrow = useMemo(() => {
    const lastUrlPath = window.location.pathname.split('/').filter(Boolean).pop();
    /**
     * Priority:
     * 1. If the product belongs to the 'certification-card-type-ecard' category, use that category name.
     * 2. Otherwise, use the category that matches the last segment of the current URL path.
     */
    const category =
      product?.categories?.find(
        (cat) =>
          cat?._url?.includes('/certification-card-type-ecard/') ||
          cat?._url?.includes('/certification-card-type-physical/'),
      ) || product?.categories?.find((cat) => cat?._url?.includes(`/${lastUrlPath}`));

    return category?.name || '';
  }, [product]);

  return (
    <div
      data-id={`product-tile-${index}`}
      onClick={onClick}
      ref={ref}
      className="product-tile group relative flex h-full flex-col overflow-hidden rounded-none border border-gray-200 bg-white shadow-sm transition delay-200 ease-linear hover:shadow-md"
    >
      <div data-id={`product-image-wrapper-${index}`} className="relative">
        <NextLink href={`/${locale}${productUrl}`} className="pb-16">
          <div data-id={`product-image-container-${index}`} className="relative w-full">
            <div data-id={`product-image-bg-${index}`} className="relative bg-white p-0">
              <div data-id={`product-image-aspect-${index}`} className="relative block aspect-[16/9] w-full">
                <Image
                  src={selectedVariant.images?.[0]}
                  alt={product.name ?? ''}
                  className="size-full object-cover group-hover:opacity-75"
                  priority={imagePriority}
                  style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                />
              </div>
            </div>
            <ProductBadge product={product} />
            <span
              data-id={`product-wishlist-btn-${index}`}
              className="absolute right-0 top-0 z-10 flex size-32 cursor-pointer items-center justify-center md:size-48"
              onClick={(e) => e.preventDefault()}
            >
              {!disableWishlistButton && !isCertificationCard && productToWishlistLineItem && (
                <WishlistButton
                  dataId="wishlist-button"
                  lineItem={productToWishlistLineItem}
                  className="size-16 md:size-20 lg:size-24"
                />
              )}
            </span>
          </div>
        </NextLink>
      </div>

      <NextLink href={`/${locale}${productUrl}`} className="flex-1">
        <div
          data-id={`product-details-${index}`}
          className="flex h-full flex-col justify-between px-8 pb-8 md:px-16 md:pb-16"
        >
          <div data-id={`product-header-${index}`} className="pt-12">
            {eyeBrow && (
              <p data-id={`product-card-group-${index}`} className="text-sm font-bold text-padi-gray-darker">
                {eyeBrow}
              </p>
            )}
            <TitleHtag
              data-id={`product-title-${index}`}
              className="mt-4 block w-full whitespace-pre-line text-wrap text-base font-semibold leading-loose"
            >
              {product?.name}
            </TitleHtag>
          </div>
          <div
            data-id={`product-excerpt-${index}`}
            className="mt-2 text-sm text-slate-700"
            dangerouslySetInnerHTML={{ __html: excerpt }}
          />
          <p data-id={`product-card-type-${index}`} className="py-12 text-sm font-bold not-italic text-slate-700">
            {cardType}
          </p>
          <div data-id={`product-prices-${index}`} className="mt-8 md:mt-12">
            <Prices price={selectedVariant?.price} discountedPrice={discountedPrice} />
          </div>
        </div>
      </NextLink>
    </div>
  );
};

export default CertProductTile;
